/**
 * Organizations API service.
 *
 * Wraps the /api/v1/organizations/ surface stood up in phase 08-04/08-05:
 * non-disclosing EIN dedup probe, dedup-gated org creation, memberships list,
 * active-org switch, request-access (join-request), and invitation acceptance.
 *
 * Frozen contract (08-04 / 08-05 SUMMARY):
 *  - POST /organizations/ein-probe/  {ein}                       -> 200 {duplicate_warning, message}
 *  - POST /organizations/            {name, legal_name, organization_type, ein,
 *                                     organizational_npi?, address?,
 *                                     create_separate?, reason?}  -> 201 {id, name, status, active_organization_id?}
 *                                                                    or 200 {duplicate_warning, message} on collision without create_separate
 *  - GET  /organizations/memberships/                            -> 200 [{organization_id, name, role, status}]
 *  - POST /organizations/switch/     {organization_id}           -> 200 {active_organization_id}
 *  - POST /organizations/join-requests/ {organization_id, reason?} -> 201 {id, status}
 *  - POST /organizations/invitations/accept/ {token}             -> 200 {organization_id, active_organization_id}
 *
 * NOTE (non-disclosure): the probe/create responses NEVER carry the matched
 * organization's identity (name/id/members/address). Callers must not invent it.
 */

import axios from './axios';

const organizationsApi = {
  /**
   * Non-disclosing EIN/TIN duplicate probe.
   * @param {string} ein - EIN/tax id (digits or formatted; server normalizes).
   * @returns {Promise<{duplicate_warning: boolean, message: string}>}
   */
  einProbe: async (ein) => {
    const response = await axios.post('/organizations/ein-probe/', { ein });
    return response.data;
  },

  /**
   * Create an organization (dedup-gated). On an EIN collision without
   * create_separate the server returns 200 {duplicate_warning, message} and
   * creates nothing; with create_separate + reason it returns 201 pending.
   * @param {object} payload
   * @returns {Promise<object>} the response body
   */
  createOrganization: async (payload) => {
    const response = await axios.post('/organizations/', payload);
    return response.data;
  },

  /**
   * List the current user's own memberships (for the org switcher).
   * @returns {Promise<Array<{organization_id, name, role, status}>>}
   */
  listMemberships: async () => {
    const response = await axios.get('/organizations/memberships/');
    return response.data;
  },

  /**
   * Switch the active organization to one the user is an ACTIVE member of.
   * @param {string} organizationId
   * @returns {Promise<{active_organization_id: string}>}
   */
  switchOrganization: async (organizationId) => {
    const response = await axios.post('/organizations/switch/', { organization_id: organizationId });
    return response.data;
  },

  /**
   * Post a request to join an existing organization (owner/admin reviews it).
   * @param {{organizationId: string, reason?: string}} args
   * @returns {Promise<{id: string, status: string}>}
   */
  createJoinRequest: async ({ organizationId, reason }) => {
    const payload = { organization_id: organizationId };
    if (reason) payload.reason = reason;
    const response = await axios.post('/organizations/join-requests/', payload);
    return response.data;
  },

  /**
   * Redeem an invitation code/token; switches the active org on success.
   * @param {string} token - raw invitation code.
   * @returns {Promise<{organization_id: string, active_organization_id: string}>}
   */
  acceptInvitation: async (token) => {
    const response = await axios.post('/organizations/invitations/accept/', { token });
    return response.data;
  },

  /**
   * Create an invitation (owner/admin only). The raw code is dispatched
   * out-of-band by the backend and is NEVER returned in the response body,
   * so the caller must not attempt to render a token.
   * @param {{invited_email: string, role?: string, organizationId?: string}} args
   * @returns {Promise<{id: string, status: string}>}
   */
  createInvitation: async ({ invited_email, role, organizationId }) => {
    const payload = { invited_email };
    if (role) payload.role = role;
    if (organizationId) payload.organization_id = organizationId;
    const response = await axios.post('/organizations/invitations/', payload);
    return response.data;
  },

  /**
   * List join-requests. `scope=incoming` requires the caller to be an
   * owner/admin of the given org (else the backend returns 404); `outgoing`
   * (default) returns the caller's own requests.
   * @param {{scope?: string, organizationId?: string}} args
   * @returns {Promise<Array<{id, organization_id, status, reason, created_at}>>}
   */
  listJoinRequests: async ({ scope = 'incoming', organizationId } = {}) => {
    const params = { scope };
    if (organizationId) params.organization_id = organizationId;
    const response = await axios.get('/organizations/join-requests/', { params });
    return response.data;
  },

  /**
   * Approve or reject a pending join-request (owner/admin only).
   * @param {string} id - join-request id.
   * @param {'approve'|'reject'} decision
   * @returns {Promise<{id: string, status: string}>}
   */
  decideJoinRequest: async (id, decision) => {
    const response = await axios.post(`/organizations/join-requests/${id}/decision/`, { decision });
    return response.data;
  },
};

export default organizationsApi;
